import { useRef, useState } from "react";
import { toast } from "sonner";
import { Upload, X, Loader2 } from "lucide-react";
import api from "@/lib/api";

/**
 * ImageUploader — uploads an image to /api/admin/upload and returns the
 * absolute URL via `onUploaded`. Supports either a single-image slot (default)
 * or a multi-image gallery when `multiple` is true.
 */
export default function ImageUploader({
  value,
  onChange,
  multiple = false,
  kind = "product",
  label = "Upload Image",
  aspect = "aspect-[4/5]",
  testid = "image-uploader",
}) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const urls = multiple ? (Array.isArray(value) ? value : []) : value ? [value] : [];

  const doUpload = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    setUploading(true);
    try {
      const results = [];
      for (const f of files) {
        const form = new FormData();
        form.append("file", f);
        form.append("kind", kind);
        const { data } = await api.post("/admin/upload", form, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        results.push(data.url);
      }
      if (multiple) {
        onChange([...(Array.isArray(value) ? value : []), ...results]);
      } else {
        onChange(results[0]);
      }
      toast.success(`Uploaded ${results.length} image${results.length > 1 ? "s" : ""}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const removeAt = (idx) => {
    if (multiple) {
      const next = urls.filter((_, i) => i !== idx);
      onChange(next);
    } else {
      onChange("");
    }
  };

  return (
    <div className="space-y-3" data-testid={testid}>
      <div className={multiple ? "grid grid-cols-3 sm:grid-cols-4 gap-2" : ""}>
        {urls.map((u, i) => (
          <div key={i} className={`relative border ${aspect} bg-muted overflow-hidden group`}>
            <img src={u} alt="" className="w-full h-full object-cover" />
            <button
              type="button"
              onClick={() => removeAt(i)}
              className="absolute top-1 right-1 h-7 w-7 grid place-items-center bg-background/90 hover:bg-background border"
              aria-label="Remove image"
              data-testid={`${testid}-remove-${i}`}
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        ))}
        {(multiple || urls.length === 0) && (
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            disabled={uploading}
            className={`border border-dashed ${aspect} flex flex-col items-center justify-center gap-2 text-xs opacity-70 hover:opacity-100 hover:border-foreground disabled:opacity-50`}
            data-testid={`${testid}-add`}
          >
            {uploading ? <Loader2 className="h-5 w-5 animate-spin" /> : <Upload className="h-5 w-5" />}
            <span className="label-xs">{uploading ? "Uploading…" : label}</span>
          </button>
        )}
      </div>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        multiple={multiple}
        onChange={(e) => doUpload(e.target.files)}
        className="hidden"
        data-testid={`${testid}-input`}
      />
    </div>
  );
}
